/**
 * Listino delle attività a tariffa.
 *
 * Non è una griglia a caselle fisse ma un elenco di voci, perché la stessa
 * tipologia di attività può avere tariffe diverse: nell'offerta ICAT la
 * giornata presso il cliente vale 500 €, quella di audit presso il fornitore
 * in Marocco 550 €. Sono entrambe front office.
 *
 * Serve a due scopi, e il titolo lo dice:
 *  - nelle offerte a economia è IL listino, con le quantità previste;
 *  - nelle offerte a corpo sono le tariffe degli interventi aggiuntivi, che
 *    per definizione non hanno quantità prevista (mostraQuantita=false).
 */
import { useMemo } from 'react';
import CampoImporto from './CampoImporto';

const UNITA = [
  { v: 'giorno', l: 'Giornata' },
  { v: 'mezza_giornata', l: 'Mezza giornata' },
  { v: 'ora', l: 'Ora' },
  // Attività che si contano a pezzo (audit a bordo, ispezioni, collaudi): il
  // prezzo è quello della singola attività e non si rendiconta il tempo.
  { v: 'intervento', l: 'Attività (a intervento)' },
];
const TIPI = [
  { v: 'front_office', l: 'Front office' },
  { v: 'back_office', l: 'Back office' },
];

// Voci ricorrenti, per non riscrivere ogni volta le stesse diciture
const SUGGERIMENTI = [
  { descrizione: 'Giornata c/o cliente', tipo: 'front_office', unita: 'giorno' },
  { descrizione: 'Back office', tipo: 'back_office', unita: 'giorno' },
  { descrizione: 'Audit c/o fornitore', tipo: 'front_office', unita: 'giorno' },
  { descrizione: 'Audit a bordo', tipo: 'front_office', unita: 'intervento' },
];

const vuota = () => ({
  descrizione: '', tipo: 'front_office', unita: 'giorno',
  tariffa_cliente: '', tariffa_consulente: '', quantita_prevista: '',
});

/**
 * Macro voce: un titolo che raggruppa le voci che lo seguono, fino al titolo
 * successivo. Nelle offerte a corpo il cliente sceglie per blocchi — "sì
 * all'audit, no alla formazione" — e un elenco piatto non diceva cosa andava
 * con cosa.
 */
const sezioneVuota = () => ({ sezione: true, descrizione: '' });
export const isSezione = (v) => !!v?.sezione;

/**
 * Compresa nell'offerta o a scelta del cliente?
 *
 * Comanda la macro voce; fuori da ogni macro voce decide la quantità, che è
 * la regola con cui sono state scritte le offerte fino a ora. Una voce che il
 * cliente ha scelto torna fra le comprese. Stessa regola del server, in
 * server/lib/attivita_listino.js: se cambia una, va cambiata l'altra.
 */
export function statoVoce(voci, i) {
  const v = voci[i];
  if (v.scelta) return { compresa: true, etichetta: 'scelta dal cliente' };
  let sez = null;
  for (let j = i - 1; j >= 0; j--) if (isSezione(voci[j])) { sez = voci[j]; break; }
  const compresa = sez && sez.opzionale !== null && sez.opzionale !== undefined
    ? !sez.opzionale
    : (v.opzionale !== null && v.opzionale !== undefined
        ? !v.opzionale : Number(v.quantita_prevista) > 0);
  return { compresa, etichetta: compresa ? 'compresa nell’offerta' : 'a richiesta del cliente' };
}

/** Le voci che stanno sotto la macro voce in posizione `i`. */
export function vociDellaSezione(voci, i) {
  const dentro = [];
  for (let j = i + 1; j < voci.length && !isSezione(voci[j]); j++) dentro.push(voci[j]);
  return dentro;
}

const eur = (n) => (n || n === 0)
  ? Number(n).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
  : '—';

export default function PannelloListino({
  voci = [], onChange, soloLettura = false,
  titolo = 'Listino attività a economia',
  descrizione = 'Una riga per ogni tariffa concordata. Con una quantità prevista l’attività è '
              + 'compresa nell’offerta; senza, resta una tariffa che il cliente potrà richiedere: '
              + 'nel documento escono in due tabelle distinte.',
  mostraQuantita = true,
}) {
  const set = (i, campo, val) => {
    const copia = voci.map((v, k) => (k === i ? { ...v, [campo]: val } : v));
    onChange(copia);
  };

  /**
   * Cambiando unità, le tariffe già scritte si riproporzionano.
   *
   * Si scrive la tariffa a giornata e poi si passa alle ore: senza questo
   * restava 520 €/ora. Giornata = 8 ore, mezza = 4; le attività a pezzo non
   * hanno un rapporto con il tempo, quindi lì non si tocca niente.
   */
  const ORE_DI = { giorno: 8, mezza_giornata: 4, ora: 1 };
  const cambiaUnita = (i, nuova) => {
    const v = voci[i];
    const vecchia = v.unita || 'giorno';
    const da = ORE_DI[vecchia], a = ORE_DI[nuova];
    const converti = (x) => {
      const n = Number(String(x ?? '').replace(',', '.'));
      if (!da || !a || !Number.isFinite(n) || n <= 0) return x;
      return String(Math.round((n / da) * a * 100) / 100).replace('.', ',');
    };
    onChange(voci.map((r, k) => k === i ? {
      ...r, unita: nuova,
      tariffa_cliente: converti(r.tariffa_cliente),
      tariffa_consulente: converti(r.tariffa_consulente),
      // La quantità prevista segue la stessa proporzione: 2 giornate
      // diventano 16 ore, non restano 2.
      quantita_prevista: (() => {
        const n = Number(String(r.quantita_prevista ?? '').replace(',', '.'));
        if (!da || !a || !Number.isFinite(n) || n <= 0) return r.quantita_prevista;
        return String(Math.round((n * da / a) * 100) / 100);
      })(),
    } : r));
  };
  const aggiungi = (preset) => onChange([...voci, { ...vuota(), ...(preset || {}) }]);
  const togli = (i) => onChange(voci.filter((_, k) => k !== i));

  const totali = useMemo(() => {
    let ricavo = 0, costo = 0, incompleta = false;
    for (const v of voci) {
      if (isSezione(v)) continue;
      const q = Number(v.quantita_prevista) || 0;
      if (!v.tariffa_cliente) incompleta = true;
      ricavo += (Number(v.tariffa_cliente) || 0) * q;
      costo += (Number(v.tariffa_consulente) || 0) * q;
    }
    return { ricavo, costo, margine: ricavo - costo, incompleta };
  }, [voci]);

  return (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-3">
      <div>
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {titolo}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{descrizione}</p>
      </div>

      {voci.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-4 bg-white rounded border border-dashed">
          Nessuna voce a listino.
        </div>
      ) : (
        <div className="space-y-2">
          {/* Ogni voce è una scheda su due righe: sopra la descrizione, sotto i
              dettagli, ciascuno con la propria etichetta. Niente colonne strette. */}
          {voci.map((v, i) => isSezione(v) ? (
            <div key={i} className="bg-brand-50/70 rounded-lg border border-brand-100 px-3 py-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-brand-700 mb-0.5">Macro voce</label>
                  <input className="input text-sm font-semibold"
                         placeholder="es. Audit interni"
                         value={v.descrizione || ''} disabled={soloLettura}
                         onChange={e => set(i, 'descrizione', e.target.value)} />
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {/* È qui che si decide se il blocco è lavoro compreso o
                        una proposta che il cliente può prendere o lasciare:
                        vale per tutte le voci che gli stanno sotto. */}
                    {mostraQuantita && (
                      <select className="input py-0.5 text-xs w-56" value={v.opzionale ? 'listino' : 'prevista'}
                              disabled={soloLettura}
                              onChange={e => set(i, 'opzionale', e.target.value === 'listino')}>
                        <option value="prevista">Attività prevista (compresa)</option>
                        <option value="listino">A listino, sceglie il cliente</option>
                      </select>
                    )}
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const dentro = vociDellaSezione(voci, i);
                        if (!dentro.length) return 'Le voci che aggiungi qui sotto le apparterranno.';
                        const val = dentro.reduce((t, x) =>
                          t + (Number(x.tariffa_cliente) || 0) * (Number(x.quantita_prevista) || 0), 0);
                        return `${dentro.length} ${dentro.length === 1 ? 'voce' : 'voci'}`
                          + (val > 0 ? ` · ${eur(val)}` : '');
                      })()}
                    </span>
                  </div>
                </div>
                {!soloLettura && (
                  <button type="button" onClick={() => togli(i)}
                          className="text-gray-400 hover:text-red-600 text-lg leading-none mt-5 px-1"
                          title="Elimina la macro voce">✕</button>
                )}
              </div>
            </div>
          ) : (
            <div key={i} className={`bg-white rounded-lg border p-3 space-y-2 ${voci.slice(0, i).some(isSezione) ? 'ml-4' : ''}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Descrizione attività</label>
                  <input
                    className="input text-sm"
                    placeholder="es. Audit c/o fornitore (Marocco)"
                    value={v.descrizione || ''} disabled={soloLettura}
                    onChange={e => set(i, 'descrizione', e.target.value)}
                  />
                </div>
                {!soloLettura && (
                  <button type="button" onClick={() => togli(i)}
                          className="text-gray-400 hover:text-red-600 text-lg leading-none mt-5 px-1"
                          title="Elimina voce">✕</button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Tipo</label>
                  <select className="input text-sm" value={v.tipo || 'front_office'}
                          disabled={soloLettura} onChange={e => set(i, 'tipo', e.target.value)}>
                    {TIPI.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Unità</label>
                  <select className="input text-sm" value={v.unita || 'giorno'}
                          disabled={soloLettura} onChange={e => cambiaUnita(i, e.target.value)}>
                    {UNITA.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Tariffa cliente (€)</label>
                  <CampoImporto className="input text-sm text-right"
                         placeholder="500" value={v.tariffa_cliente ?? ''} disabled={soloLettura}
                         onChange={val => set(i, 'tariffa_cliente', val)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Tariffa consulente (€)</label>
                  <CampoImporto className="input text-sm text-right"
                         placeholder="facoltativa" value={v.tariffa_consulente ?? ''} disabled={soloLettura}
                         onChange={val => set(i, 'tariffa_consulente', val)} />
                </div>
                {mostraQuantita && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-0.5">Q.tà prevista</label>
                    <input className="input text-sm text-right" type="number" step="0.5"
                           placeholder={v.unita === 'ora' ? 'ore' : v.unita === 'intervento' ? 'n.' : 'gg'}
                           value={v.quantita_prevista ?? ''} disabled={soloLettura}
                           onChange={e => set(i, 'quantita_prevista', e.target.value)} />
                    {/* Compresa o a richiesta: lo dice la macro voce se c'è,
                        altrimenti la quantità. Nel documento finiscono in due
                        tabelle diverse, e il cliente le legge come due cose
                        diverse. */}
                    {(() => {
                      const stato = statoVoce(voci, i);
                      // Fuori dalle comprese, la spunta registra cosa il
                      // cliente ha davvero preso: da quel momento è lavoro
                      // come gli altri ed entra nell'importo.
                      if (stato.compresa && !v.scelta) {
                        return <p className="text-[11px] mt-0.5 text-green-700">{stato.etichetta}</p>;
                      }
                      return (
                        <label className="flex items-center gap-1 mt-0.5 text-[11px] cursor-pointer">
                          <input type="checkbox" className="w-3 h-3" checked={!!v.scelta}
                                 disabled={soloLettura}
                                 onChange={e => set(i, 'scelta', e.target.checked)} />
                          <span className={v.scelta ? 'text-green-700' : 'text-amber-700'}>
                            {v.scelta ? 'scelta dal cliente' : 'scelta dal cliente?'}
                          </span>
                        </label>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!soloLettura && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" className="btn-secondary btn-sm" onClick={() => aggiungi()}>+ Voce</button>
          <button type="button" className="btn-secondary btn-sm"
                  title="Un titolo che raggruppa le voci che lo seguono"
                  onClick={() => onChange([...voci, sezioneVuota()])}>+ Macro voce</button>
          <span className="text-xs text-gray-400 mx-1">oppure</span>
          {SUGGERIMENTI.map(s => (
            <button key={s.descrizione} type="button" className="btn-secondary btn-sm"
                    onClick={() => aggiungi(s)}>+ {s.descrizione}</button>
          ))}
        </div>
      )}

      {/* Senza quantità previste non c'è un valore da totalizzare: le tariffe
          degli interventi aggiuntivi valgono solo se il cliente li chiede. */}
      {voci.length > 0 && mostraQuantita && (
        <div className="border-t pt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="text-gray-600">Valore previsto: <strong className="text-gray-900">{eur(totali.ricavo)}</strong></span>
          {totali.costo > 0 && (
            <>
              <span className="text-gray-600">Costo consulenti: <strong className="text-gray-900">{eur(totali.costo)}</strong></span>
              <span className="text-gray-600">Margine: <strong className="text-gray-900">{eur(totali.margine)}</strong></span>
            </>
          )}
          {/* I totali si basano sulle quantità previste: senza tariffa o senza
              quantità il valore mostrato è per forza parziale, e va detto. */}
          {totali.ricavo === 0 && (
            <span className="text-xs text-gray-400">Indica le quantità previste per calcolare il valore.</span>
          )}
          {totali.incompleta && totali.ricavo > 0 && (
            <span className="text-xs text-amber-700">Alcune voci non hanno tariffa cliente: escluse dal totale.</span>
          )}
        </div>
      )}
    </div>
  );
}
